import _ from 'lodash'
import React from 'react'
import classNames from 'classnames'
import moment from 'moment'

import { actionIdByName } from 'mednet-util/src/constants/questionUpdate'

import { Body1, Header3, Subtitle2, Subtitle3 } from 'pharmacy/src/typography'
import { Card } from 'pharmacy/src/display/card'
import { Button } from 'pharmacy/src/input/button'
import { UserUpdateInformation } from 'pharmacy/src/user/userUpdateInformation'
import { QuestionLink } from 'pharmacy/src/navigation/questionLink'
import { QuestionPoll } from 'pharmacy/src/question/questionPoll'

import { connect } from 'react-redux'

import { fetchViewsFromSpecialty } from 'mednet-cns/src/reducers/question'
import { FETCH_UPDATE_IN_NOTIFICATION_USAGE } from 'mednet-cns/src/reducers/questionUpdate'
import { makeRequestName } from 'mednet-cns/src/reducers/request'

import { LastUsedInfo, QuestionUsageLine } from '../lastUsedInfo'
import { QuestionTopicTags } from '../questionTopicTags'

import css from './updateCard.scss'

class UpdateCard extends React.Component {
  state = {
    showMore: false,
  }

  toggleMore = () => {
    const { update, specialtyId, fetchViewsFromSpecialty, question } =
      this.props

    this.setState((state) => ({
      showMore: !state.showMore,
    }))

    if (
      !question.views ||
      (!question.views[specialtyId] && question.views[specialtyId] !== 0)
    ) {
      fetchViewsFromSpecialty(update.questionId, specialtyId)
    }
  }

  onAddUpdate = () => {
    const { onAddUpdate, update } = this.props
    if (onAddUpdate) {
      onAddUpdate(update.questionUpdateId, update.questionId)
    }
  }

  renderViews = () => {
    const { specialtyId, specialtyTitle, question } = this.props

    if (
      !question.views ||
      (!question.views[specialtyId] && question.views[specialtyId] !== 0)
    )
      return null

    return (
      <div className={css.stats}>
        <Subtitle2>{`Unique views in ${specialtyTitle}: ${question.views[specialtyId]}`}</Subtitle2>
        <Subtitle2>{`Unique views in total: ${question.views.total}`}</Subtitle2>
      </div>
    )
  }

  renderPoll() {
    const { update } = this.props
    const { showMore } = this.state

    if (update.actionId !== actionIdByName.POLL_APPROVED || !showMore) {
      return null
    }

    const { poll } = update
    poll.showResults = true
    poll.showVote = false

    return (
      <QuestionPoll
        questionId={update.questionId}
        poll={poll}
        className={css.poll}
        HeaderComponent={Header3}
        hideHeaderInfo
        hideExportToCSV
      />
    )
  }

  render() {
    const {
      update,
      className,
      updateIsLoading,
      hideInfo,
      onAddUpdate,
      specialtyId,
      specialtyTitle,
      updateUsageLoaded,
      updateUsageLoading,
      updateUsageError,
    } = this.props
    const { showMore } = this.state
    const question = update.question || this.props.question

    let details
    let body
    let infoLabel
    // Date/verb come from the answer, not the update row: search shows one card
    // per answer, but its update row may be an older in-range revision, not the
    // latest. Taking both from one source keeps them in step — a revised answer
    // shows "Updated answer on <revision date>"; an unrevised one shows "Answered
    // on <created date>".
    let dateUpdated
    let dateActionId = update.actionId

    // This card's own item, so the "another item" line can exclude/label it. Keyed
    // by content id + type bucket, matching the question-usage payload.
    let ownContentId
    let ownType

    if (
      update.actionId === actionIdByName.ANSWER ||
      update.actionId === actionIdByName.ANSWER_UPDATED
    ) {
      details = update.answer
      body = details.answer
      infoLabel = 'answer'
      dateUpdated = details.updated || details.created
      dateActionId = details.updated
        ? actionIdByName.ANSWER_UPDATED
        : actionIdByName.ANSWER
      ownContentId = details.answerId
      ownType = 'answer'
    } else if (update.actionId === actionIdByName.POLL_APPROVED) {
      details = update.poll
      body = details.pollQuestion
      infoLabel = 'poll'
      dateUpdated = details.created
      ownContentId = details.pollId
      ownType = 'poll'
    } else if (update.actionId === actionIdByName.QUESTION_APPROVED) {
      // A question-approval card has no answer/poll body of its own — the question
      // text renders through the QuestionLink header below, so we leave `details`
      // unset (its body block stays hidden). We still set the own item so the "another
      // item" line can label this question's own rows and surface sibling sends.
      infoLabel = 'question'
      ownContentId = question.questionId
      ownType = 'question'
    }

    const shortBody =
      (body && body.length) > 150 ? `${body.substring(0, 149)}...` : body

    if (update.actionId !== actionIdByName.POLL_APPROVED) {
      body = showMore ? body : shortBody
    } else {
      body = showMore ? '' : shortBody
    }

    const activeSponsorships = question.sponsorships?.filter((sponsorship) =>
      moment(new Date()).isBetween(sponsorship.startDate, sponsorship.endDate)
    )

    return (
      <Card
        className={classNames({
          [css.card]: true,
          [className]: true,
          [css.sponsored]: Boolean(activeSponsorships?.length),
        })}
      >
        <div className={css.questionWrapper}>
          <div className={css.questionContent}>
            {hideInfo ? null : (
              <>
                <LastUsedInfo
                  updateId={update.questionUpdateId}
                  lastRevised={update.answer?.updated}
                  label={infoLabel}
                  className={css.lastUsedInfo}
                  mode="dailydigest"
                  specialtyId={specialtyId}
                  specialtyTitle={specialtyTitle}
                />
                {!ownType ? null : (
                  <QuestionUsageLine
                    specialtyId={specialtyId}
                    questionId={question.questionId}
                    ownContentId={ownContentId}
                    ownType={ownType}
                    // The "last used" chip and this line share the update-usage fetch
                    // state; hide the line until that prerequisite resolves so the two
                    // never show two loaders, and drop it entirely if that fetch failed
                    // (the primary check is what matters). Mirror the chip's own loading
                    // test: the request flag is page-shared, so a stale `isLoaded` can
                    // linger from an earlier batch while THIS page's fetch is in flight
                    // — check `isLoading` too, or the line renders while the chip still
                    // shows its spinner.
                    hidden={
                      !updateUsageLoaded ||
                      updateUsageLoading ||
                      updateUsageError
                    }
                  />
                )}
              </>
            )}
            <QuestionLink questionId={question.questionId} target="_blank">
              <Header3
                className={css.question}
                dangerouslySetInnerHTML={{
                  __html: question.short ? question.short : question.question,
                }}
              ></Header3>
            </QuestionLink>
            <QuestionTopicTags
              topics={question.topics}
              primary={question.primaryTopic}
            />
            {!showMore ? null : this.renderViews()}
          </div>
          {!onAddUpdate ? null : (
            <Button
              className={css.addButton}
              size="small"
              icon={['fal', 'plus']}
              onClick={this.onAddUpdate}
              isDisabled={updateIsLoading}
            />
          )}
        </div>
        {!details ? null : (
          <UserUpdateInformation
            user={details.user}
            dateUpdated={dateUpdated}
            actionId={dateActionId}
          >
            <Body1
              dangerouslySetInnerHTML={{
                __html: body,
              }}
            ></Body1>
          </UserUpdateInformation>
        )}
        {this.renderPoll()}
        <div className={css.showMore}>
          <Subtitle3>
            <Button
              type="secondaryText"
              size="medium"
              onClick={this.toggleMore}
            >
              {showMore ? 'show less' : 'show more'}
            </Button>
          </Subtitle3>
        </div>
      </Card>
    )
  }
}

const mapStateToProps = (state, ownProps) => {
  const updateUsageRequest =
    _.get(
      state.request.requests,
      makeRequestName(FETCH_UPDATE_IN_NOTIFICATION_USAGE)
    ) || {}
  return {
    question: state.question.questions[ownProps.update.questionId] || {},
    updateUsageLoaded: updateUsageRequest.isLoaded,
    updateUsageLoading: updateUsageRequest.isLoading,
    updateUsageError: updateUsageRequest.isError,
  }
}

const mapDispatchToProps = (dispatch) => ({
  fetchViewsFromSpecialty: (questionId, specialtyId) =>
    dispatch(fetchViewsFromSpecialty(questionId, specialtyId)),
})

export default connect(mapStateToProps, mapDispatchToProps)(UpdateCard)
